import { useId, useState } from "react";

import { FormFieldShell } from "@/components/forms/FormField";
import { FormRichText } from "@/components/forms/FormRichText";
import { Heading } from "@/components/typography";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/input-group";
import { toast } from "@/components/ui/toast";
import { analyticsService } from "../services/analytics";
import { updatePitchFromEdit } from "../services/firebase";
import { formatMoney, getCurrencySymbol } from "@/shared/currency";
import type { Campaign, Currency, Pitch, Talent, User } from "../shared/types";
import { pitchPriceWithinCampaignBounds } from "../utils/pitching/eligibility";
import { canEditPitch } from "../utils/pitching/permissions";
import { tiptapJsonToPlainText } from "../shared/tiptapText";

type EditPitch = Pick<Pitch, "id" | "talentName" | "price" | "notes" | "status" | "managerId" | "bookingId"> & {
    talent?: Pick<Talent, "managerIds">;
};

type EditPitchCampaign = Pick<Campaign, "currency" | "minPricePerCreator" | "maxPricePerCreator">;

type EditPitchDialogProps = {
    pitch: EditPitch | null;
    campaign?: EditPitchCampaign;
    user: User | undefined;
    onOpenChange: (open: boolean) => void;
};

/** Rate and notes while a pitch is still open. Saving a needs-info pitch returns it to pitched. */
export function EditPitchDialog({ pitch, campaign, user, onOpenChange }: EditPitchDialogProps) {
    const [pending, setPending] = useState(false);
    const currency = campaign?.currency || "USD";
    const minPrice = campaign?.minPricePerCreator;
    const maxPrice = campaign?.maxPricePerCreator;

    function handleOpenChange(open: boolean) {
        if (!open && pending) {
            return;
        }
        onOpenChange(open);
    }

    async function save(price: number, notes: Record<string, unknown> | null) {
        if (!pitch || !campaign || pitch.bookingId || !canEditPitch(user, pitch, pitch.talent)) {
            return;
        }
        const priceInRange = price > 0 && pitchPriceWithinCampaignBounds(price, minPrice, maxPrice);
        if (!priceInRange) {
            toast.add({
                type: "error",
                title: "Price is outside this opportunity’s range",
                description: "Update the rate and try again.",
            });
            return;
        }
        setPending(true);
        try {
            const result = await updatePitchFromEdit(pitch.id, price, notes);
            if (!result.success) {
                console.error(result.error);
                toast.add({
                    type: "error",
                    title: "Couldn’t save this pitch",
                    description: "Refresh and try again.",
                });
                return;
            }
            onOpenChange(false);
        } catch (error) {
            console.error(error);
            analyticsService().logError(error);
            toast.add({
                type: "error",
                title: "Couldn’t update this pitch",
                description: "Refresh and try again.",
            });
        } finally {
            setPending(false);
        }
    }

    return (
        <Dialog open={pitch != null} onOpenChange={handleOpenChange}>
            {/* Cap to the viewport: header stays put, fields scroll, actions stay at the bottom. */}
            <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden sm:max-w-lg">
                <DialogHeader className="shrink-0">
                    <DialogTitle render={<Heading type="h3" tag="h2" />}>
                        Edit pitch
                    </DialogTitle>
                    {pitch?.talentName ? (
                        <DialogDescription className="text-foreground">
                            {atHandle(pitch.talentName)}
                        </DialogDescription>
                    ) : null}
                </DialogHeader>
                {pitch ? (
                    <EditPitchForm
                        key={pitch.id}
                        pitch={pitch}
                        currency={currency}
                        minPrice={minPrice}
                        maxPrice={maxPrice}
                        actionPending={pending}
                        onCancel={() => handleOpenChange(false)}
                        onSubmit={save}
                    />
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

function EditPitchForm({
    pitch,
    currency,
    minPrice,
    maxPrice,
    actionPending,
    onCancel,
    onSubmit,
}: {
    pitch: EditPitch;
    currency: Currency;
    minPrice?: number;
    maxPrice?: number;
    actionPending: boolean;
    onCancel: () => void;
    onSubmit: (price: number, notes: Record<string, unknown> | null) => void;
}) {
    const rateId = useId();
    const [priceInput, setPriceInput] = useState(String(pitch.price));
    const [notes, setNotes] = useState<Record<string, unknown> | undefined>(pitch.notes);
    const [priceError, setPriceError] = useState<string>();
    const bounds = priceBoundsCopy(minPrice, maxPrice, currency);

    function submit() {
        const price = Number.parseInt(priceInput, 10);
        const parsed = Number.isNaN(price) ? null : price;
        const error = rateError(parsed, minPrice, maxPrice);
        if (error || parsed == null) {
            setPriceError(error || "Enter a price.");
            return;
        }
        setPriceError(undefined);
        const storedNotes = notes && tiptapJsonToPlainText(notes) ? notes : null;
        onSubmit(parsed, storedNotes);
    }

    return (
        <form
            className="flex min-h-0 flex-col"
            onSubmit={(event) => {
                event.preventDefault();
                submit();
            }}
        >
            {/* Padding keeps focus rings inside the scroll clip; negative margin keeps field alignment. */}
            <div className="-m-1 min-h-0 space-y-4 overflow-y-auto p-1">
                <FormFieldShell
                    label="Rate"
                    required
                    htmlFor={rateId}
                    description={bounds}
                    error={priceError}
                    invalid={Boolean(priceError)}
                    disabled={actionPending}
                >
                    {(controlProps) => (
                        <InputGroup>
                            <InputGroupAddon align="inline-start">
                                <InputGroupText>{getCurrencySymbol(currency)}</InputGroupText>
                            </InputGroupAddon>
                            <InputGroupInput
                                {...controlProps}
                                disabled={actionPending}
                                className={
                                    "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none " +
                                    "[&::-webkit-outer-spin-button]:appearance-none"
                                }
                                value={priceInput}
                                type="number"
                                inputMode="numeric"
                                aria-invalid={priceError ? true : undefined}
                                aria-label="Rate"
                                onChange={(event) => {
                                    setPriceInput(event.target.value);
                                    setPriceError(undefined);
                                }}
                                onWheel={(event) => event.currentTarget.blur()}
                            />
                        </InputGroup>
                    )}
                </FormFieldShell>
                <FormRichText
                    label="Notes"
                    value={notes}
                    onChange={setNotes}
                    disabled={actionPending}
                />
            </div>
            <DialogFooter className="sticky bottom-0 shrink-0 bg-popover mt-4 pt-4">
                <Button type="button" variant="secondary" disabled={actionPending} onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" disabled={actionPending}>
                    Save
                </Button>
            </DialogFooter>
        </form>
    );
}

function atHandle(name: string) {
    return name.startsWith("@") ? name : `@${name}`;
}

function priceBoundsCopy(minPrice: number | undefined, maxPrice: number | undefined, currency: string) {
    const minLabel = formatMoney(minPrice, currency);
    const maxLabel = formatMoney(maxPrice, currency);
    if (minLabel && maxLabel) {
        return `Must be between ${minLabel} and ${maxLabel}.`;
    }
    if (minLabel) {
        return `Must be at least ${minLabel}.`;
    }
    if (maxLabel) {
        return `Must be at most ${maxLabel}.`;
    }
    return undefined;
}

function rateError(price: number | null, minPrice?: number, maxPrice?: number) {
    if (price == null || price <= 0) {
        return "Enter a price.";
    }
    if (!pitchPriceWithinCampaignBounds(price, minPrice, maxPrice)) {
        return "Enter a price within this opportunity’s range.";
    }
    return undefined;
}
